import {startOfDay} from 'date-fns';
import {Form, FormField, FormSubmitHandler} from 'platform/components';
import {Grid, GridItem} from 'platform/foundation';
import {z} from 'zod';

import {isNil, isNotNil} from 'ramda';
import {isDate, isValidDate} from 'ramda-adjunct';

import {
  RentalOrderResponseBody,
  useChangeRentalOrderVehicleMutation,
  useCreateRentalOrderMutation,
  useReserveRentalOrderVehicleMutation,
} from '@dms/api/rental';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {Nullish, parseDate} from 'shared/web';

import {RentalVehicleFormValues} from '../types/RentalVehicleFormValues';
import {VehicleList} from './VehicleList';

interface RentalVehicleSelectProps {
  rentalOrder?: RentalOrderResponseBody | undefined;
  onAfterSubmit: (rentalOrderId?: string) => void;
}

export function RentalVehicleSelect(props: RentalVehicleSelectProps) {
  const isCreatingRentalOrder = isNil(props.rentalOrder?.rentalOrderId);
  const isFinishingRentalOrder = isNotNil(props.rentalOrder?.customerId);

  const [createRentalOrder] = useCreateRentalOrderMutation();
  const [updateRentalOrderVehicle] = useChangeRentalOrderVehicleMutation();
  const [reserveRentalVehicle] = useReserveRentalOrderVehicleMutation();

  const handleSubmit: FormSubmitHandler<RentalVehicleFormValues> = async (values) => {
    const submitBody = {
      rentalPeriod: {
        from: values.from?.toISOString() ?? null,
        to: values.to?.toISOString() ?? null,
      },
      rentalVehicleId: values.vehicleId,
    };

    if (isCreatingRentalOrder) {
      await createRentalOrder({
        createRentalOrderRequestBody: {
          customerId: null,
          ...submitBody,
        },
      })
        .unwrap()
        .then((data) => props.onAfterSubmit(data.id))
        .catch(handleApiError);

      return;
    }

    await updateRentalOrderVehicle({
      rentalOrderId: props.rentalOrder!.rentalOrderId,
      changeRentalOrderVehicleRequestBody: submitBody,
    })
      .unwrap()
      .then(() => props.onAfterSubmit())
      .then(async () => {
        if (isFinishingRentalOrder && props.rentalOrder?.state === 'draft') {
          await reserveRentalVehicle({
            rentalOrderId: props.rentalOrder!.rentalOrderId,
          })
            .unwrap()
            .catch(handleApiError);
        }
      })
      .catch(handleApiError);
  };

  const defaultValues: Partial<RentalVehicleFormValues> = {
    from: toDefaultDate(props.rentalOrder?.plannedRentalPeriod?.from),
    to: toDefaultDate(props.rentalOrder?.plannedRentalPeriod?.to),
    vehicleId: props.rentalOrder?.rentalVehicleId ?? undefined,
  };

  return (
    <Form<RentalVehicleFormValues>
      onSubmit={handleSubmit}
      experimentalZodSchema={schema}
      defaultValues={defaultValues}
    >
      {(control, formApi) => {
        const fromDate = formApi.watch('from');
        const minToDate = isDate(fromDate) ? startOfDay(fromDate) : undefined;

        return (
          <Grid columns={2} align="flex-end">
            <FormField
              name="from"
              type="dateTime"
              control={control}
              isRequired
              label={i18n.t('page.rentalOrder.labels.rent')}
            />
            <FormField
              name="to"
              type="dateTime"
              control={control}
              minDate={minToDate}
              isRequired
              label={i18n.t('page.rentalOrder.labels.return')}
            />

            <GridItem span={2}>
              <VehicleList
                control={control}
                formApi={formApi}
                data-testid={testIds.rentalOrder.vehicleAndCustomer('vehicleList')}
              />
            </GridItem>
          </Grid>
        );
      }}
    </Form>
  );
}

const schema = z
  .object({
    from: z.date(),
    to: z.date(),
    vehicleId: z.string(),
  })
  .refine((data) => data.from < data.to, {
    message: i18n.t('page.rentalOrder.errors.toBeforeFromError'),
    path: ['from'],
  });

/** `parseDate` can return an Invalid Date for malformed input — normalize that to undefined */
const toDefaultDate = (value: string | Nullish) => {
  const date = parseDate(value);

  return isValidDate(date) ? date : undefined;
};
